import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Loader2,
    RefreshCw,
    Send,
    ExternalLink,
    ShieldAlert,
    Copy,
    Wallet,
    MessageCircle,
    TestTube2,
    Search,
    KeyRound,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const EXPLORERS = {
    USDT_TRC20: (h) => `https://tronscan.org/#/transaction/${h}`,
    USDT_ERC20: (h) => `https://etherscan.io/tx/${h}`,
    USDC_ERC20: (h) => `https://etherscan.io/tx/${h}`,
};

const ADDRESS_EXPLORERS = {
    USDT_TRC20: (a) => `https://tronscan.org/#/address/${a}`,
    USDT_ERC20: (a) => `https://etherscan.io/address/${a}`,
    USDC_ERC20: (a) => `https://etherscan.io/address/${a}`,
};

function formatAmount(n, dec = 4) {
    const max = Math.max(0, dec);
    const min = Math.min(2, max);
    return Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: min,
        maximumFractionDigits: max,
    });
}

export default function AdminSweep() {
    const [balances, setBalances] = useState([]);
    const [history, setHistory] = useState([]);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [testingTelegram, setTestingTelegram] = useState(false);
    const [savingWallets, setSavingWallets] = useState(false);
    const [coldWalletDraft, setColdWalletDraft] = useState({
        USDT_TRC20: "",
        USDT_ERC20: "",
        USDC_ERC20: "",
    });
    const [sweeping, setSweeping] = useState({});
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const [recoveryAddress, setRecoveryAddress] = useState("");
    const [recoveryCurrency, setRecoveryCurrency] = useState("");
    const [recoveryTxHash, setRecoveryTxHash] = useState("");
    const [recoveryLoading, setRecoveryLoading] = useState(false);
    const [recoveryResult, setRecoveryResult] = useState(null);
    const [mnemonicPassword, setMnemonicPassword] = useState("");
    const [mnemonicOpen, setMnemonicOpen] = useState(false);
    const [mnemonicLoading, setMnemonicLoading] = useState(false);
    const [revealedMnemonic, setRevealedMnemonic] = useState("");
    const [addressKeyPassword, setAddressKeyPassword] = useState("");
    const [addressKeyLoading, setAddressKeyLoading] = useState(false);
    const [revealedAddressKey, setRevealedAddressKey] = useState("");

    const loadBalances = async () => {
        try {
            const { data } = await api.get("/admin/sweep/balances?user_limit=80", { timeout: 30000 });
            setBalances(data);
            setLastRefreshedAt(new Date().toISOString());
        } catch (err) {
            if (String(err?.code || "").includes("ECONNABORTED")) {
                toast.error("Veri yenileme uzun sürdü. Lütfen tekrar deneyin.");
                return;
            }
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) || "Failed",
            );
        }
    };

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/admin/sweep/history");
            setHistory(data);
        } catch {
            /* ignore */
        }
    };

    const loadOverview = async () => {
        try {
            const { data } = await api.get("/admin/wallet-ops/overview");
            setOverview(data);
            setColdWalletDraft({
                USDT_TRC20: data?.cold_wallets?.USDT_TRC20 || "",
                USDT_ERC20: data?.cold_wallets?.USDT_ERC20 || "",
                USDC_ERC20: data?.cold_wallets?.USDC_ERC20 || "",
            });
        } catch {
            /* ignore */
        }
    };

    const refreshAll = useCallback(async () => {
        setLoading(true);
        await Promise.allSettled([loadBalances(), loadHistory(), loadOverview()]);
        setLoading(false);
    }, []);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    const copyText = async (value) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            toast.success("Adres kopyalandı");
        } catch {
            toast.error("Kopyalama başarısız");
        }
    };

    const sendTelegramTest = async () => {
        setTestingTelegram(true);
        try {
            await api.post("/admin/telegram/test", { note: "Admin panelinden test" });
            toast.success("Telegram test mesajı gönderildi");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Telegram testi başarısız");
        } finally {
            setTestingTelegram(false);
        }
    };

    const saveColdWallets = async () => {
        setSavingWallets(true);
        try {
            const payload = {
                cold_wallets: {
                    USDT_TRC20: coldWalletDraft.USDT_TRC20?.trim() || null,
                    USDT_ERC20: coldWalletDraft.USDT_ERC20?.trim() || null,
                    USDC_ERC20: coldWalletDraft.USDC_ERC20?.trim() || null,
                },
            };
            await api.patch("/admin/wallet-ops/config", payload);
            toast.success("Ana cüzdan adresleri güncellendi");
            await loadOverview();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Cüzdan kaydedilemedi");
        } finally {
            setSavingWallets(false);
        }
    };

    const sweepOne = async (b) => {
        const key = `${b.user_id}-${b.currency}-${b.address}`;
        setSweeping((s) => ({ ...s, [key]: true }));
        try {
            const { data } = await api.post("/admin/sweep", {
                user_id: b.user_id,
                currency: b.currency,
            });
            toast.success(
                `Swept ${formatAmount(data.amount)} ${b.currency.replace(
                    "_",
                    " ",
                )} → soğuk cüzdan`,
            );
            loadBalances();
            loadHistory();
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Çekim işlemi başarısız",
            );
        } finally {
            setSweeping((s) => ({ ...s, [key]: false }));
        }
    };

    const runAddressRecovery = async (e) => {
        e.preventDefault();
        if (!recoveryAddress.trim()) {
            toast.error("Adres girin");
            return;
        }
        setRecoveryLoading(true);
        setRecoveryResult(null);
        try {
            const { data } = await api.post("/admin/recovery/address", {
                address: recoveryAddress.trim(),
                currency: recoveryCurrency || undefined,
                tx_hash: recoveryTxHash.trim() || undefined,
            });
            setRecoveryResult(data);
            toast.success("Adres kurtarma analizi tamamlandı");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Kurtarma analizi başarısız");
        } finally {
            setRecoveryLoading(false);
        }
    };

    const revealMnemonic = async (e) => {
        e.preventDefault();
        if (!mnemonicPassword.trim()) {
            toast.error("Admin şifrenizi girin");
            return;
        }
        setMnemonicLoading(true);
        try {
            const { data } = await api.post("/admin/wallet-ops/reveal-mnemonic", {
                password: mnemonicPassword,
                context_address: recoveryAddress.trim() || undefined,
            });
            setRevealedMnemonic(data?.mnemonic || "");
            setMnemonicOpen(true);
            setMnemonicPassword("");
            toast.success("Kurtarma tümceciği gösterildi");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Şifre doğrulama başarısız");
        } finally {
            setMnemonicLoading(false);
        }
    };

    const revealAddressKey = async (e) => {
        e.preventDefault();
        if (!recoveryAddress.trim()) {
            toast.error("Önce adres girin");
            return;
        }
        if (!addressKeyPassword.trim()) {
            toast.error("Admin şifrenizi girin");
            return;
        }
        setAddressKeyLoading(true);
        try {
            const { data } = await api.post("/admin/wallet-ops/reveal-address-key", {
                address: recoveryAddress.trim(),
                currency: recoveryCurrency || undefined,
                password: addressKeyPassword,
            });
            setRevealedAddressKey(data?.private_key_hex || "");
            setAddressKeyPassword("");
            toast.success("Adres private key gösterildi");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Adres private key alınamadı");
        } finally {
            setAddressKeyLoading(false);
        }
    };

    const totalUsd = balances.reduce((acc, b) => acc + Number(b.balance || 0), 0);
    const actionableBalances = balances.filter((b) => b.actionable);
    const watchlistBalances = balances.filter((b) => !b.actionable);

    return (
        <div data-testid="admin-sweep" className="space-y-8">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                        Çekim Merkezi
                    </h1>
                    <p className="text-muted-foreground mt-1.5 max-w-xl">
                        Kullanıcıların HD deposit adreslerine gelen USDT/USDC
                        bakiyelerini tek tıkla ana soğuk cüzdanlara çekin.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/admin/wallets" data-testid="sweep-open-wallet-addresses">
                        <Button
                            variant="outline"
                            className="border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                        >
                            <Wallet className="w-4 h-4 mr-2" /> Cüzdan adreslerini yönet
                        </Button>
                    </Link>
                    <Button
                        variant="outline"
                        onClick={() => {
                            refreshAll();
                        }}
                        disabled={loading}
                        data-testid="sweep-refresh"
                        className="border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Verileri yenile
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4" data-testid="sweep-overview-cards">
                <div className="bg-card border border-white/[0.07] rounded-2xl p-5 space-y-3">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                        Ana soğuk cüzdanlar
                    </p>
                    {[
                        ["USDT_TRC20", overview?.cold_wallets?.USDT_TRC20],
                        ["USDT_ERC20", overview?.cold_wallets?.USDT_ERC20],
                        ["USDC_ERC20", overview?.cold_wallets?.USDC_ERC20],
                    ].map(([code, addr]) => (
                        <div key={code} className="space-y-1" data-testid={`cold-wallet-${code}`}>
                            <p className="text-xs font-semibold">{code.replace("_", " ")}</p>
                            <div className="flex items-center gap-2">
                                <Input
                                    value={coldWalletDraft[code] || ""}
                                    onChange={(e) => setColdWalletDraft((s) => ({ ...s, [code]: e.target.value }))}
                                    data-testid={`cold-wallet-input-${code}`}
                                    placeholder={code === "USDT_TRC20" ? "T..." : "0x..."}
                                    className="text-[11px] font-mono h-9 bg-background border-white/10"
                                />
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => copyText(coldWalletDraft[code] || addr)}
                                    disabled={!(coldWalletDraft[code] || addr)}
                                    data-testid={`copy-cold-wallet-${code}`}
                                    className="border-white/10"
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                            {!addr && (
                                <p className="text-[11px] text-amber-400" data-testid={`cold-wallet-missing-${code}`}>
                                    Henüz tanımlı değil
                                </p>
                            )}
                        </div>
                    ))}
                    <Button
                        onClick={saveColdWallets}
                        disabled={savingWallets}
                        data-testid="save-cold-wallets-button"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        {savingWallets ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Kaydediliyor
                            </>
                        ) : (
                            "Ana cüzdanları kaydet"
                        )}
                    </Button>
                </div>

                <div className="bg-card border border-white/[0.07] rounded-2xl p-5 space-y-3">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                        HD ana cüzdanlar (index 0)
                    </p>
                    {[
                        ["USDT_TRC20", overview?.master_wallets?.USDT_TRC20],
                        ["USDT_ERC20", overview?.master_wallets?.USDT_ERC20],
                        ["USDC_ERC20", overview?.master_wallets?.USDC_ERC20],
                    ].map(([code, addr]) => (
                        <div key={code} className="space-y-1" data-testid={`master-wallet-${code}`}>
                            <p className="text-xs font-semibold">{code.replace("_", " ")}</p>
                            <div className="flex items-center gap-2">
                                <code className="text-[11px] font-mono text-muted-foreground break-all bg-background border border-white/10 rounded px-2 py-1.5 flex-1">
                                    {addr || "Henüz üretilemedi"}
                                </code>
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => copyText(addr)}
                                    disabled={!addr}
                                    data-testid={`copy-master-wallet-${code}`}
                                    className="border-white/10"
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-card border border-white/[0.07] rounded-2xl p-5 space-y-4">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                            Telegram bildirimleri
                        </p>
                        <p
                            className="text-sm mt-2 inline-flex items-center gap-2"
                            data-testid="telegram-status"
                        >
                            <MessageCircle className="w-4 h-4 text-primary" />
                            {overview?.telegram?.configured
                                ? `Aktif (${overview.telegram.chat_id})`
                                : "Pasif"}
                        </p>
                    </div>
                    <Button
                        onClick={sendTelegramTest}
                        disabled={testingTelegram || !overview?.telegram?.configured}
                        data-testid="telegram-test-button"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        {testingTelegram ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Test gönderiliyor
                            </>
                        ) : (
                            <>
                                <TestTube2 className="w-4 h-4 mr-2" />
                                Telegram test mesajı gönder
                            </>
                        )}
                    </Button>

                    <div className="text-xs text-muted-foreground space-y-1">
                        <p data-testid="gas-eth-status">
                            ETH gas otomasyonu: {overview?.gas_automation?.eth_enabled ? "Açık" : "Kapalı"}
                        </p>
                        <p data-testid="gas-tron-status">
                            TRON gas otomasyonu: {overview?.gas_automation?.tron_enabled ? "Açık" : "Kapalı"}
                        </p>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4" data-testid="withdraw-guide">
                <h3 className="font-semibold mb-2">Gelen USDT&apos;yi nasıl çekersiniz?</h3>
                {lastRefreshedAt && (
                    <p className="text-xs text-muted-foreground mb-2" data-testid="sweep-last-refreshed">
                        Son yenileme: {new Date(lastRefreshedAt).toLocaleString("tr-TR")}
                    </p>
                )}
                <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                    <li>Önce <strong>Verileri yenile</strong> butonuyla ağ bakiyelerini güncelleyin.</li>
                    <li><strong>Süpürülebilir Bakiyeler</strong> tablosunda kullanıcı satırındaki <strong>Sweep</strong> butonuna basın.</li>
                    <li>Transfer tamamlanınca hash, aşağıdaki <strong>Çekim Geçmişi</strong> bölümünde görünür.</li>
                </ol>
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl p-5 space-y-4" data-testid="address-recovery-panel">
                <div>
                    <h3 className="font-display font-semibold text-lg">Adres Kurtarma Merkezi</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Belirli bir adres/tx için on-chain bakiye, kullanıcı eşleşmesi ve sweep uygunluk raporu.
                    </p>
                </div>
                <form onSubmit={runAddressRecovery} className="grid lg:grid-cols-4 gap-2 items-end" data-testid="address-recovery-form">
                    <div className="lg:col-span-2 space-y-1">
                        <label className="text-xs text-muted-foreground">Adres</label>
                        <Input
                            value={recoveryAddress}
                            onChange={(e) => setRecoveryAddress(e.target.value)}
                            placeholder="0x... veya T..."
                            data-testid="recovery-address-input"
                            className="font-mono bg-background border-white/10"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Ağ (opsiyonel)</label>
                        <select
                            value={recoveryCurrency}
                            onChange={(e) => setRecoveryCurrency(e.target.value)}
                            data-testid="recovery-currency-select"
                            className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 h-10 text-sm"
                        >
                            <option value="">Otomatik</option>
                            <option value="USDT_TRC20">USDT_TRC20</option>
                            <option value="USDT_ERC20">USDT_ERC20</option>
                            <option value="USDC_ERC20">USDC_ERC20</option>
                        </select>
                    </div>
                    <Button type="submit" data-testid="recovery-run-btn" disabled={recoveryLoading} className="bg-primary text-primary-foreground">
                        {recoveryLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Tara
                    </Button>
                    <div className="lg:col-span-4 space-y-1">
                        <label className="text-xs text-muted-foreground">TxID (opsiyonel)</label>
                        <Input
                            value={recoveryTxHash}
                            onChange={(e) => setRecoveryTxHash(e.target.value)}
                            placeholder="0x..."
                            data-testid="recovery-tx-input"
                            className="font-mono bg-background border-white/10"
                        />
                    </div>
                </form>

                {recoveryResult && (
                    <div className="rounded-xl border border-white/10 bg-background/40 p-4 text-xs space-y-2" data-testid="recovery-result-panel">
                        <p data-testid="recovery-next-action"><strong>Öneri:</strong> {recoveryResult.next_action}</p>
                        <p data-testid="recovery-user-match"><strong>Kullanıcı:</strong> {recoveryResult?.matched_user?.email || "Bulunamadı"}</p>
                        <p data-testid="recovery-balance-max"><strong>Maks bakiye:</strong> {formatAmount(recoveryResult.max_balance || 0, 6)}</p>
                        <p data-testid="recovery-sweep-supported"><strong>Otomatik sweep:</strong> {recoveryResult.sweep_supported ? "Uygun" : "Uygun değil"}</p>
                        {!!recoveryResult?.onchain_tx_matches?.length && (
                            <p data-testid="recovery-onchain-match" className="text-emerald-400">
                                ✅ Tx zincirde bu adreste bulundu: {recoveryResult.onchain_tx_matches[0]?.amount} {recoveryResult.onchain_tx_matches[0]?.currency}
                            </p>
                        )}
                        {!recoveryResult.sweep_supported && (
                            <p className="text-amber-400" data-testid="recovery-unsupported-reason">{recoveryResult.unsupported_reason}</p>
                        )}
                    </div>
                )}

                <div className="rounded-xl border border-red-500/20 bg-red-500/[0.06] p-4" data-testid="mnemonic-reveal-panel">
                    <p className="text-sm font-semibold inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Kurtarma tümceciği (yüksek risk)</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Admin şifresiyle açılır. Bu tümceciği gören kişi fonları taşıyabilir.
                    </p>
                    <form onSubmit={revealMnemonic} className="mt-3 flex gap-2" data-testid="mnemonic-reveal-form">
                        <Input
                            type="password"
                            value={mnemonicPassword}
                            onChange={(e) => setMnemonicPassword(e.target.value)}
                            placeholder="Admin şifresi"
                            data-testid="mnemonic-password-input"
                            className="bg-background border-white/10"
                        />
                        <Button type="submit" data-testid="mnemonic-reveal-btn" disabled={mnemonicLoading} className="bg-primary text-primary-foreground">
                            {mnemonicLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Göster
                        </Button>
                    </form>

                    {mnemonicOpen && !!revealedMnemonic && (
                        <div className="mt-3 rounded-lg border border-red-500/30 bg-black/50 p-3" data-testid="mnemonic-revealed-box">
                            <p className="text-[11px] text-red-300 mb-2">⚠️ Ekran görüntüsü almayın. Güvenli offline yere taşıyın.</p>
                            <code className="font-mono text-xs break-words select-all">{revealedMnemonic}</code>
                        </div>
                    )}

                    <form onSubmit={revealAddressKey} className="mt-4 space-y-2" data-testid="address-key-reveal-form">
                        <p className="text-xs text-muted-foreground">Adres private key (tek adres kurtarma)</p>
                        <div className="flex gap-2">
                            <Input
                                type="password"
                                value={addressKeyPassword}
                                onChange={(e) => setAddressKeyPassword(e.target.value)}
                                placeholder="Admin şifresi"
                                data-testid="address-key-password-input"
                                className="bg-background border-white/10"
                            />
                            <Button type="submit" data-testid="address-key-reveal-btn" disabled={addressKeyLoading} className="bg-primary text-primary-foreground">
                                {addressKeyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Private key göster
                            </Button>
                        </div>
                        {!!revealedAddressKey && (
                            <div className="rounded-lg border border-red-500/30 bg-black/50 p-3" data-testid="address-key-revealed-box">
                                <p className="text-[11px] text-red-300 mb-2">⚠️ Bu key ile fon taşınabilir.</p>
                                <code className="font-mono text-xs break-words select-all">{revealedAddressKey}</code>
                            </div>
                        )}
                    </form>
                </div>
            </div>

            {/* Risk banner */}
            <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/[0.04] p-4 flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <div className="text-sm text-foreground/85">
                    <p className="font-semibold mb-1">Operatör notu</p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground text-[13px]">
                        <li>
                            Her sweep işlemi, kullanıcıya ait türetilmiş private key ile
                            anlık imzalanır. Onaylamadan önce hedef adresin sizin soğuk
                            cüzdanınız olduğunu kontrol edin.
                        </li>
                        <li>
                            ERC-20 sweep için ETH gas gerekir. <code>COLD_WALLET_ETH_PK</code>{" "}
                            tanımlı değilse kaynağa manuel ETH gönderin.
                        </li>
                        <li>
                            TRC-20 sweep için ~15 TRX gerekir. <code>COLD_WALLET_TRON_PK</code>{" "}
                            varsa otomatik gas gönderilir, yoksa manuel fonlayın.
                        </li>
                    </ul>
                </div>
            </div>

            {/* Pending balances */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-semibold text-lg">
                        Süpürülebilir bakiyeler
                    </h3>
                    <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                        {actionableBalances.length} süpürülebilir · {watchlistBalances.length} izleme · {formatAmount(totalUsd, 2)} stablecoin (~${formatAmount(totalUsd, 0)})
                    </span>
                </div>
                {actionableBalances.length === 0 ? (
                    <div className="bg-card border border-white/[0.07] rounded-2xl p-10 text-center text-sm text-muted-foreground">
                        Şu anda çekilecek bakiye bulunmuyor.
                    </div>
                ) : (
                    <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground border-b border-white/5">
                                <tr>
                                    <th className="text-left py-3 px-4">Kullanıcı</th>
                                    <th className="text-left py-3 px-4">Para birimi</th>
                                    <th className="text-right py-3 px-4">Bakiye</th>
                                    <th className="text-left py-3 px-4">Kaynak</th>
                                    <th className="text-right py-3 px-4 pr-5">İşlem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {actionableBalances.map((b) => {
                                    const key = `${b.user_id}-${b.currency}-${b.address}`;
                                    return (
                                        <tr
                                            key={key}
                                            data-testid={`sweep-row-${b.user_id}-${b.currency}-${b.address.slice(-6)}`}
                                        >
                                            <td className="py-3 px-4">
                                                <p className="font-medium truncate max-w-[200px]">
                                                    {b.user_name || "—"}
                                                </p>
                                                <p className="text-[11px] font-mono text-muted-foreground truncate max-w-[200px]">
                                                    {b.user_email}
                                                </p>
                                                {!!b.source_tags?.length && (
                                                    <p
                                                        className="text-[10px] text-primary mt-1"
                                                        data-testid={`sweep-source-tags-${key}`}
                                                    >
                                                        kaynak: {b.source_tags.join(", ")}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[11px] font-mono">
                                                    {b.currency.replace("_", " ")}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-right font-display font-semibold tabular-nums">
                                                {formatAmount(b.balance)}
                                            </td>
                                            <td className="py-3 px-4">
                                                <code className="text-[11px] font-mono text-muted-foreground">
                                                    {b.address.slice(0, 6)}…
                                                    {b.address.slice(-6)}
                                                </code>
                                                <p className="text-[10px] text-muted-foreground mt-1" data-testid={`gas-status-${key}`}>
                                                    gas: {formatAmount(b.native_gas_balance || 0, 6)} {b.native_gas_asset} / gerekli {formatAmount(b.native_gas_required || 0, 6)}
                                                </p>
                                            </td>
                                            <td className="py-3 px-4 pr-5 text-right">
                                                <div className="inline-flex items-center gap-2">
                                                    <a
                                                        href={ADDRESS_EXPLORERS[b.currency]?.(b.address) || "#"}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`sweep-address-link-${b.user_id}-${b.currency}`}
                                                        className="text-xs text-muted-foreground hover:text-foreground"
                                                    >
                                                        Adresi aç
                                                    </a>
                                                    <Button
                                                        onClick={() => sweepOne(b)}
                                                        disabled={!!sweeping[key] || !b.actionable}
                                                        data-testid={`sweep-btn-${b.user_id}-${b.currency}-${b.address.slice(-6)}`}
                                                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-9 px-4"
                                                    >
                                                        {sweeping[key] ? (
                                                            <>
                                                                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                                                Çekiliyor
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Send className="w-3.5 h-3.5 mr-2" />
                                                                Sweep
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-semibold text-lg">İzleme listesi (otomatik süpürme dışı)</h3>
                    <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{watchlistBalances.length} adres</span>
                </div>
                {watchlistBalances.length === 0 ? (
                    <div className="bg-card border border-white/[0.07] rounded-2xl p-6 text-center text-sm text-muted-foreground">
                        İzleme listesinde adres yok.
                    </div>
                ) : (
                    <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground border-b border-white/5">
                                <tr>
                                    <th className="text-left py-3 px-4">Kullanıcı</th>
                                    <th className="text-left py-3 px-4">Para birimi</th>
                                    <th className="text-right py-3 px-4">Token</th>
                                    <th className="text-right py-3 px-4">Gas</th>
                                    <th className="text-left py-3 px-4">Sebep</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {watchlistBalances.map((b, idx) => (
                                    <tr key={`${b.user_id}-${b.currency}-${idx}`} data-testid={`watchlist-row-${idx}`}>
                                        <td className="py-3 px-4 text-xs font-mono">{b.user_email || b.user_id}</td>
                                        <td className="py-3 px-4">{b.currency.replace("_", " ")}</td>
                                        <td className="py-3 px-4 text-right font-semibold">{formatAmount(b.balance, 6)}</td>
                                        <td className="py-3 px-4 text-right">{formatAmount(b.native_gas_balance || 0, 6)} {b.native_gas_asset}</td>
                                        <td className="py-3 px-4 text-xs text-amber-400">{b.unsupported_reason || (!b.has_enough_gas ? "Gas yetersiz" : "Actionable değil")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* History */}
            <div>
                <h3 className="font-display font-semibold text-lg mb-4">
                    Çekim geçmişi
                </h3>
                {history.length === 0 ? (
                    <div className="bg-card border border-white/[0.07] rounded-2xl p-8 text-center text-sm text-muted-foreground">
                        Henüz çekim yapılmadı.
                    </div>
                ) : (
                    <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden divide-y divide-white/5">
                        {history.map((h) => (
                            <div
                                key={h.id}
                                data-testid={`sweep-history-${h.id}`}
                                className="px-5 py-3.5 flex items-center justify-between gap-4"
                            >
                                <div className="min-w-0">
                                    <p className="font-medium text-sm truncate">
                                        {formatAmount(h.amount)}{" "}
                                        {h.currency.replace("_", " ")} → soğuk cüzdan
                                    </p>
                                    <p className="text-[11px] font-mono text-muted-foreground mt-0.5">
                                        {new Date(h.created_at).toLocaleString()} ·
                                        from {h.from_address?.slice(0, 8)}…
                                    </p>
                                </div>
                                <a
                                    href={EXPLORERS[h.currency]?.(h.tx_hash) || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[11px] font-mono text-primary hover:text-primary/80 inline-flex items-center gap-1 shrink-0"
                                >
                                    tx{" "}
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
